import type { PersistentUserSession } from '@mimir/client-common/util/persistentUserSession';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import type { Route } from 'vue-router/types/router';
import type { StoreType } from './state';
import { isClientConfig } from '@mimir/common/dto/ClientConfigDto';
import type { AppRoutingState } from '@mimir/web/sharedState/appRoutingState';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import { updateGoogleMapPromise } from '@mimir/web/globals/variables';
import type { QuickPreview } from '@mimir/web/model/quickPreviewOps';
import type { TimedMdf } from '@mimir/web/model/timedMdf';
import type { PlayerServices } from '@mimir/web/model/playerServices';
import { listenToPostMessage, notifyMimirIsLoaded, trySetUpPostMessageRelatedState } from '@mimir/web/util/postMessageHelpers';
import { createItemMediaPlayer } from '@mimir/web/model/createItemMediaPlayer';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { ItemDetailsState } from '../model/itemDetailsState';

export async function initializeStore(
  serviceCache: ServiceCache,
  session: PersistentUserSession,
  store: StoreType,
  appRoutingState: AppRoutingState,
  destinationRoute: Route,
  applicationContext: ApplicationContext,
  quickPreview: QuickPreview,
  timedMdf: TimedMdf,
  itemDetailsState: ItemDetailsState,
  playerServices: PlayerServices,
) {
  /*
    Initializes and bootstraps the store and the rest of the per-tenant global
    state, which is only available once a user is signed in. State that
    components may read before sign-in — the global backend client in
    particular — is initialized at the application entry point instead.
  */
  await initialize(serviceCache, store);
  const bootstrap = await import('../bootstrap');
  await bootstrap.bootstrap(
    applicationContext,
    session,
    store,
    quickPreview,
    timedMdf,
    destinationRoute
  );

  appRoutingState.storeInitialized = true;

  const tenantConfig: TenantConfigDto = store.getters.tenantConfig;

  const itemDetailsPageMediaPlayerOps = createItemMediaPlayer(
    playerServices,
    tenantConfig.featureFlags.useCutterPlayer ?? false,
    { useEditProxy: false, id: 'itemDetailsPage', pollBufferedIntervalMs: 500 }
  );

  itemDetailsState.setPlayer(itemDetailsPageMediaPlayerOps);

  const quickPreviewMediaPlayerOps = createItemMediaPlayer(
    playerServices,
    tenantConfig.featureFlags.useCutterPlayer,
    { id: 'quickPreview', pollBufferedIntervalMs: 500 }
  );

  quickPreview.ops.setPlayer(quickPreviewMediaPlayerOps);

  if (trySetUpPostMessageRelatedState(applicationContext)) {
    listenToPostMessage(applicationContext, quickPreview);
    notifyMimirIsLoaded();
  }
}

async function loadGoogleMap(apiKey: string | null, store: StoreType) {

  try {
    if (!apiKey) {
      return;
    }
    //If google is blocked, then we can't load the library
    const { Loader } = await import('@googlemaps/js-api-loader');
    const loader = new Loader({
      apiKey,
      libraries: ['places']
    });
    await loader.load();
  } catch (err) {
    console.error('Error loading Google Maps API', err);
  }
}

const initialize = async (serviceCache: ServiceCache, store: StoreType) => {
  const serviceDocument = await serviceCache.getServiceDocument();

  if (!serviceDocument) {
    throw new Error('No service document found.');
  }
  const clientConfig = serviceDocument.clientConfig;
  if (!isClientConfig(clientConfig)) {
    throw new Error('No clientConfig found.');
  }

  updateGoogleMapPromise(loadGoogleMap(clientConfig.googleMapsApiKey, store));
};
